import { useEffect, useRef } from 'react'

import { resetDocumentTypes, selectDocumentTypes, setDocumentTypes, setDocumentTypesLoading } from '../../../store/processDocument/documentTypes'
import { resetDocumentFilters, setDocumentContactsFilter } from '../../../store/processDocument/documentFilter'
import { resetContactUpdate, setContactChanges } from '../../../store/meetingSummary/contactUpdate'
import { useLazyGetAllContactChangesQuery } from '../../../store/meetingSupport/meetingSupport'
import { useLazyGetAllDocumentTypesQuery } from '../../../store/ai/documentTypes'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useLazyGetDocumentByIdQuery, useLazyGetDocumentsByBatchQuery } from '../../../store/ai/documents'
import { setMessages } from '../../../store/streamingChat/streamingChat'
import { addToast } from '../../../store/toast/toastSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useLazyGetAllUsersQuery, useLazyGetUserByIdQuery } from '../../../store/user/user'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  resetDocumentBatch,
  selectDocumentBatchDocuments,
  setDocumentBatchDocuments,
  setDocumentBatchContact,
  setDocumentBatchContactIsLoading,
  setDocumentBatchCreator,
  setDocumentBatchCreatorIsLoading,
  setDocumentBatchLoading,
  setDocumentBatchChatAiAgentId,
  setDocumentBatchChatResultLoadFailed,
  setDocumentBatchChatResultLoading,
} from '../../../store/processDocument/documentBatch'
import {
  setDocumentLoading,
  setDocument,
  selectDocument,
  setDocumentCreator,
  setSelectedDocumentType,
  setDocumentCreatorIsLoading,
  resetDocument,
  setDocumentFileInfoIsLoading,
  setDocumentFileInfo,
  setDocumentIsNotRelatedToClient,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryResult,
  setDocumentSummaryResultLoading,
  setDocumentSummarySyncStatus,
} from '../../../store/processDocument/document'

import { DocumentSummaryExecutionResult } from '../../../models/ProcessDocument'
import { AIAgentType } from '../../../models/AIAgent'

import { getDocumentFile, isSameDocumentFile } from '../utils'
import { useDocumentFile } from '../hooks'

export const useInitializeDocumentTypes = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAllDocumentTypes] = useLazyGetAllDocumentTypesQuery()

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        dispatch(setDocumentTypesLoading(true))
        const { data: documentTypes } = await triggerGetAllDocumentTypes({ accountId })
        if (!documentTypes?.data) {
          return
        }
        dispatch(setDocumentTypes(documentTypes.data))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentTypesLoading(false))
      }
    }

    fetchDocumentTypes()

    return () => {
      dispatch(resetDocumentTypes())
    }
  }, [accountId, dispatch, triggerGetAllDocumentTypes])
}

export const useInitializeDocumentSummary = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  useEffect(() => {
    const fetchDocumentSummary = async () => {
      dispatch(setDocumentSummaryResultLoading(true))
      try {
        const { data: aiAgentResult } = await triggerGetAiAgentResult({ accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: documentId })
        if (!aiAgentResult) {
          return
        }

        const { originalAiAgentId, result, interaction, syncStatus } = aiAgentResult
        const { aiAgentId } = interaction ?? {}

        const { summary, documentIsNotRelatedToClient } = result as DocumentSummaryExecutionResult

        dispatch(setDocumentSummaryAiAgentId(aiAgentId ?? originalAiAgentId))
        dispatch(setDocumentSummaryResult({ summary }))
        dispatch(setDocumentSummarySyncStatus(syncStatus))

        if (documentIsNotRelatedToClient) {
          dispatch(setDocumentIsNotRelatedToClient(true))
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentSummaryResultLoading(false))
      }
    }

    fetchDocumentSummary()
  }, [accountId, dispatch, documentId, triggerGetAiAgentResult])
}

export const useInitializeDocumentBatch = (documentBatchId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetDocumentsByBatch] = useLazyGetDocumentsByBatchQuery()

  useEffect(() => {
    const fetchDocumentBatch = async () => {
      try {
        dispatch(setDocumentBatchLoading(true))

        const documents = await triggerGetDocumentsByBatch({ accountId, documentBatchId }).unwrap()
        if (!documents) {
          dispatch(addToast({ variant: 'error', messages: ['This package could not be found or is no longer available.'] }))
          return
        }

        dispatch(setDocumentBatchDocuments(documents))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentBatchLoading(false))
      }
    }

    fetchDocumentBatch()

    return () => {
      dispatch(resetDocumentBatch())
    }
  }, [accountId, dispatch, documentBatchId, triggerGetDocumentsByBatch])
}

export const useInitializeDocumentBatchChat = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const documents = useAppSelector(selectDocumentBatchDocuments)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const documentBatchId = documents[0]?.documentBatchId ?? null

  useEffect(() => {
    if (!documentBatchId) {
      return
    }

    const fetchDocumentBatchChat = async () => {
      dispatch(setDocumentBatchChatResultLoading(true))
      try {
        const aiAgentResult = await triggerGetAiAgentResult({
          accountId,
          type: AIAgentType.DOCUMENT_BATCH_CHAT,
          resultKey: `documentBatchChat|${documentBatchId}`,
        }).unwrap()
        if (!aiAgentResult) {
          return
        }

        const { originalAiAgentId, interaction } = aiAgentResult
        const { aiAgentId, interactions = [] } = interaction ?? {}

        dispatch(setDocumentBatchChatAiAgentId(aiAgentId ?? originalAiAgentId))
        dispatch(setMessages(interactions.map((i) => i.result)))
      } catch (e) {
        console.error(e)
        dispatch(setDocumentBatchChatResultLoadFailed(true))
      } finally {
        dispatch(setDocumentBatchChatResultLoading(false))
      }
    }

    fetchDocumentBatchChat()
  }, [accountId, dispatch, documentBatchId, triggerGetAiAgentResult])
}

export const useInitializeDocumentBatchUsers = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const documents = useAppSelector(selectDocumentBatchDocuments)

  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  const creatorId = documents[0]?.createdBy.id ?? null
  const contactId = documents[0]?.contactId ?? null

  useEffect(() => {
    if (!creatorId || !contactId) {
      return
    }

    const fetchDocumentBatchUsers = async () => {
      dispatch(setDocumentBatchCreatorIsLoading(true))
      dispatch(setDocumentBatchContactIsLoading(true))
      try {
        const { data: usersResponse } = await triggerGetAllUsers({ accountId, ids: [creatorId, contactId] })
        if (!usersResponse) {
          return
        }

        const { data: users } = usersResponse

        const creator = users.find((user) => user.id === creatorId)
        if (creator) {
          dispatch(setDocumentBatchCreator(creator))
        }

        const contact = users.find((user) => user.id === contactId)
        if (contact) {
          dispatch(setDocumentBatchContact(contact))
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentBatchCreatorIsLoading(false))
        dispatch(setDocumentBatchContactIsLoading(false))
      }
    }

    fetchDocumentBatchUsers()
  }, [accountId, dispatch, creatorId, contactId, triggerGetAllUsers])
}

export const useInitializeDocument = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const documentTypes = useAppSelector(selectDocumentTypes)

  const [triggerGetDocumentById] = useLazyGetDocumentByIdQuery()

  useEffect(() => {
    if (!documentTypes.length) {
      return
    }

    const fetchDocument = async () => {
      dispatch(setDocumentLoading(true))
      try {
        const document = await triggerGetDocumentById({ accountId, documentId }).unwrap()
        if (!document) {
          dispatch(addToast({ variant: 'error', messages: ['This document could not be found or is no longer available.'] }))
          return
        }

        dispatch(setDocument(document))

        const { typeId } = document.typeProcessing

        const documentType = documentTypes.find((documentType) => documentType.id === typeId)
        if (!documentType) {
          return
        }

        dispatch(setSelectedDocumentType(documentType))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentLoading(false))
      }
    }

    fetchDocument()

    return () => {
      dispatch(resetDocument())
    }
  }, [accountId, dispatch, documentId, documentTypes, triggerGetDocumentById])
}

export const useInitializeDocumentCreator = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)

  const [triggerGetUserById] = useLazyGetUserByIdQuery()

  const creatorId = document?.createdBy.id ?? null

  useEffect(() => {
    if (!creatorId) {
      return
    }

    const fetchDocumentCreator = async () => {
      dispatch(setDocumentCreatorIsLoading(true))

      try {
        const { data: creator } = await triggerGetUserById({ accountId, userId: creatorId }).unwrap()
        dispatch(setDocumentCreator(creator))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentCreatorIsLoading(false))
      }
    }

    fetchDocumentCreator()
  }, [accountId, dispatch, creatorId, triggerGetUserById])
}

export const useInitializeContactChanges = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAllContactChanges] = useLazyGetAllContactChangesQuery()

  useEffect(() => {
    dispatch(setContactChanges([]))

    const fetchContactChanges = async () => {
      try {
        const { data: contactChanges } = await triggerGetAllContactChanges({ accountId, filters: { typeKey: documentId, type: 'document' } }).unwrap()
        dispatch(setContactChanges(contactChanges))
      } catch (e) {
        console.error(e)
      }
    }
    fetchContactChanges()
  }, [accountId, dispatch, documentId, triggerGetAllContactChanges])

  useEffect(() => {
    return () => {
      dispatch(resetContactUpdate())
    }
  }, [dispatch])
}

export const useInitializeBatchForDocument = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)

  const [triggerGetDocumentsByBatch] = useLazyGetDocumentsByBatchQuery()

  const documentBatchIdRef = useRef(document?.documentBatchId ?? null)
  if (document && document.documentBatchId !== documentBatchIdRef.current) {
    documentBatchIdRef.current = document.documentBatchId
  }
  const documentBatchId = documentBatchIdRef.current

  useEffect(() => {
    if (!documentBatchId) {
      return
    }

    const fetchDocumentBatch = async () => {
      try {
        dispatch(setDocumentBatchLoading(true))

        const documents = await triggerGetDocumentsByBatch({ accountId, documentBatchId }).unwrap()
        if (!documents) {
          return
        }

        dispatch(setDocumentBatchDocuments(documents))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentBatchLoading(false))
      }
    }

    fetchDocumentBatch()

    return () => {
      dispatch(resetDocumentBatch())
    }
  }, [accountId, dispatch, documentBatchId, triggerGetDocumentsByBatch])
}

export const useInitializeDocumentFilters = (contactIds?: string[]) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (contactIds?.length) {
      dispatch(setDocumentContactsFilter(contactIds))
    }

    return () => {
      dispatch(resetDocumentFilters())
    }
  }, [contactIds, dispatch])
}

export const useInitializeDocumentFileInfo = () => {
  const dispatch = useAppDispatch()
  const document = useAppSelector(selectDocument)
  const { fetchDocumentFile } = useDocumentFile()

  const file = getDocumentFile(document)

  const fileRef = useRef(file)
  if (!isSameDocumentFile(fileRef.current, file)) {
    fileRef.current = file
  }
  const documentFile = fileRef.current

  useEffect(() => {
    if (!documentFile) {
      return
    }

    const fetchFileInfo = async () => {
      dispatch(setDocumentFileInfoIsLoading(true))
      try {
        const fileInfo = await fetchDocumentFile(documentFile)
        if (!fileInfo) {
          return
        }

        dispatch(setDocumentFileInfo(fileInfo))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentFileInfoIsLoading(false))
      }
    }

    fetchFileInfo()
  }, [dispatch, documentFile, fetchDocumentFile])
}
